import { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import * as yup from 'yup';

import { trans, clientErrorTracker } from '@spotahome/soyuz/client';

import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import { useCookie } from '@spotahome/ui-library';
import createZendeskTicket, {
  createZendeskTicketLogged
} from '@spotahome/ui-library/src/tenant/mutations/zendesk';

import ContactUsHelp from '@spotahome/tenant-common/src/components/ContactUsHelp';

import FormProvider from '@spotahome/landings-common/src/components/FormProvider';
import FormError from '@spotahome/landings-common/src/components/FormError';
import { useStateMachineContext } from '@spotahome/landings-common/src/context/StateMachineContext';
import graphqlClient from '@spotahome/landings-common/src/graphql/client';

import {
  TENANT_REASONS,
  FORM_FIELDS,
  TENANT_CONFIG_NEW
} from '../../constants';

import ContactUsHeader from '../ContactUsHeader';
import { applyActions } from '../../utils/actions';

import './ContactForm.scss';

const FORM_COMPONENTS = {
  text: {
    component: FormProvider.Input,
    props: {
      type: 'text'
    }
  },
  number: {
    component: FormProvider.Input,
    props: {
      type: 'number'
    }
  },
  email: {
    component: FormProvider.Input,
    props: {
      type: 'email'
    }
  },
  date: {
    component: FormProvider.Date
  },
  textarea: {
    component: FormProvider.Input,
    props: {
      rows: 4,
      type: 'textarea'
    }
  },
  select: {
    component: FormProvider.Select
  },
  cityAutocompleter: {
    component: FormProvider.CityAutocompleter
  },
  phone: {
    component: FormProvider.PhoneInput
  },
  dependentSelectors: {
    component: FormProvider.DependentSelectors
  },
  fileUploaderMulti: {
    component: FormProvider.FileUploaderMulti,
    props: {
      id: 'attachmentPaths',
      name: 'attachmentPaths',
      required: false,
      maxFiles: 10,
      onFileUploadError: error => {
        clientErrorTracker.error({ error, source: 'ContactForm-UploadFiles' });
      }
    }
  }
};

const composeFormSchema = (formFields, conditions) => {
  const schemaObject = formFields.reduce((acc, fieldId) => {
    const fieldCondition = conditions[fieldId];
    if (fieldCondition) {
      const { dependsOn, compare } = fieldCondition;
      acc[fieldId] = FORM_FIELDS[fieldId].schema.when(dependsOn, {
        is: compare,
        then: yup.string().ensure().required('contact-us.required-field')
      });
    } else {
      acc[fieldId] = FORM_FIELDS[fieldId].schema;
    }

    return acc;
  }, {});

  return yup.object().shape(schemaObject);
};

const ContactForm = ({ isPublicForm = false }) => {
  const [submitError, setSubmitError] = useState(false);
  const [loading, setLoading] = useState(false);
  const {
    send,
    payload: { role = 'tenant', categoryId = null }
  } = useStateMachineContext();

  const [reasonConfig, setReasonConfig] = useState({});

  const { getCookie } = useCookie();

  const {
    helpCenterArticles = [],
    fields: formFields = [],
    sosNumber = null,
    conditions = {},
    postActions = []
  } = reasonConfig;

  const reasons = categoryId
    ? TENANT_CONFIG_NEW[categoryId]
    : Object.keys(TENANT_REASONS);

  const formSchema = composeFormSchema(formFields, conditions);

  const scrollTop = () => window && window.scrollTo({ top: 0 });

  const formChangeHandler = (value, input) => {
    if (input === 'reason') {
      setReasonConfig(TENANT_REASONS[value] ?? {});
    }
  };

  const handleFormSubmit = async values => {
    const {
      reason,
      city,
      referenceCode,
      bookingId,
      name,
      email,
      rentableUnitId,
      listingUrl,
      moveInDate,
      moveOutDate,
      comment,
      numberProperties,
      phoneNumber,
      phoneNumberOptional,
      budget,
      device,
      browser,
      tenantProfile,
      neighbourhood,
      propertyType,
      idNumber,
      address,
      listingModificationType,
      newContactDetails,
      unpublishReason,
      cancellationReason,
      doubtMotive,
      doubtSubmotive,
      attachmentPaths
    } = values;

    setSubmitError(false);
    setLoading(true);

    if (doubtMotive && doubtSubmotive) {
      SoyuzAnalytics.sendGA4Event('send', {
        section: 'listing-doubts',
        motive: doubtMotive,
        submotive: doubtSubmotive
      });
    }

    const input = {
      reason,
      role,
      phoneNumber: phoneNumber || phoneNumberOptional,
      numberProperties,
      referenceCode,
      bookingId,
      city: city ? city.id : null,
      country: city ? city.country.countryId : null,
      rentableUnitId,
      listingUrl,
      moveInDate,
      moveOutDate,
      comment,
      budget,
      device,
      browser,
      tenantProfile,
      neighbourhood,
      propertyType,
      idNumber,
      address,
      listingModificationType,
      newContactDetails,
      unpublishReason,
      cancellationReason,
      doubtMotive,
      doubtSubmotive,
      sourceSegment: getCookie('tenantSupportSource') || null,
      attachmentPaths
    };

    try {
      if (isPublicForm) {
        await createZendeskTicket(graphqlClient())({
          input: {
            ...input,
            name,
            email
          }
        });
      } else {
        await createZendeskTicketLogged(graphqlClient())({ input });
      }

      await applyActions(postActions);

      setLoading(false);
      send('NEXT', { ...values, sosNumber });
      scrollTop();
    } catch (error) {
      setLoading(false);
      setSubmitError(true);
      clientErrorTracker.error({ error, source: 'ContactForm' });
    }
  };

  return (
    <div className="contact-form">
      <ContactUsHeader
        title={trans('contact-us.form.back')}
        onBack={() => send('BACK')}
      />

      <h1 className="contact-form__title">{trans('contact-us.form.title')}</h1>
      <p className="contact-form__description">
        {trans('contact-us.form.description')}
      </p>

      <div className="contact-form__content">
        <div className="contact-form__form-section">
          <FormProvider
            schema={formSchema}
            onSubmit={handleFormSubmit}
            onChange={formChangeHandler}
          >
            {({ values }) => (
              <Fragment>
                <FormProvider.Select
                  name="reason"
                  title={trans('contact-us.field.reason.title')}
                  placeholder={trans(
                    'contact-us.tenant.form.reason.placeholder'
                  )}
                  options={reasons.map(key => ({
                    value: key,
                    text: trans(`contact-us.tenant.new.${key}`)
                  }))}
                />
                {formFields
                  .map(fieldId => ({
                    id: fieldId,
                    ...FORM_FIELDS[fieldId]
                  }))
                  .filter(fieldConfig => {
                    const fieldCondition = conditions[fieldConfig.id];
                    if (fieldCondition) {
                      const { compare, dependsOn } = fieldCondition;
                      const compareArgs = dependsOn.map(dep => values[dep]);
                      return compare(...compareArgs);
                    }
                    return true;
                  })
                  .map(({ id, type, props }) => {
                    const Component = FORM_COMPONENTS[type].component;
                    return (
                      <Component
                        id={id}
                        key={id}
                        name={id}
                        title={trans(`contact-us.field.${id}.title`)}
                        placeholder={trans(
                          `contact-us.field.${id}.placeholder`
                        )}
                        {...FORM_COMPONENTS[type].props}
                        {...props}
                      />
                    );
                  })}
                <div className={'contact-form__submit-container'}>
                  <div className={'contact-form__submit-button'}>
                    <FormProvider.Submit isLoading={loading}>
                      {loading
                        ? trans('loading')
                        : trans('contact-us.form.submit')}
                    </FormProvider.Submit>
                  </div>
                  {submitError && (
                    <div className={'contact-form__submit-error'}>
                      <FormError error={trans('contact-us.form-error')} />
                    </div>
                  )}
                </div>
              </Fragment>
            )}
          </FormProvider>
        </div>

        <div className="contact-form__help-section">
          <ContactUsHelp helpCenterArticles={helpCenterArticles} />
        </div>
      </div>
    </div>
  );
};

ContactForm.propTypes = {
  isPublicForm: PropTypes.bool
};

export default ContactForm;
